import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Check, Sparkles } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { OnboardingModal } from '@/components/onboarding/OnboardingModal';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import auraLogo from '@/assets/aura-logo.png';
import appScreenshot from '@/assets/app-screenshot.png';

interface AuraLandingPageProps {
  onGetStarted: () => void;
}

const SCHEMA_DATA = [
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "AURA",
    "applicationCategory": "LifestyleApplication",
    "operatingSystem": "Web, iOS, Android",
    "description": "AI that dresses you. Digitize your wardrobe. Get styled in seconds.",
    "url": "https://www.myaura.vip",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "INR"
    },
  }
];

// Animated underline component - the signature motif
function GoldUnderline({ delay = 0, className = "" }: { delay?: number; className?: string }) {
  return (
    <motion.span 
      className={`absolute bottom-0 left-0 h-[2px] bg-gradient-to-r from-amber-400 to-amber-600 ${className}`}
      initial={{ width: 0 }}
      whileInView={{ width: "100%" }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    />
  );
}

// Section wrapper with fade-in
function Section({ children, className = "", id }: { children: React.ReactNode; className?: string; id?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      id={id}
      className={className}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.section>
  );
}

export const AuraLandingPage: React.FC<AuraLandingPageProps> = ({ onGetStarted }) => {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const headerBg = useTransform(scrollYProgress, [0, 0.1], [0, 1]);

  const handleGetStarted = () => setShowOnboarding(true);
  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    onGetStarted();
  };

  return (
    <>
      <Helmet>
        <title>AURA — AI That Dresses You</title>
        <meta name="description" content="Stop staring at your closet. AURA digitizes your wardrobe and styles you in seconds. Free to start." />
        <meta property="og:title" content="AURA — AI That Dresses You" />
        <meta property="og:description" content="Digitize your wardrobe. Get styled in seconds." />
        <link rel="canonical" href="https://www.myaura.vip" />
        {SCHEMA_DATA.map((schema, index) => (
          <script key={index} type="application/ld+json">{JSON.stringify(schema)}</script>
        ))}
      </Helmet>
      
      <div ref={containerRef} className="min-h-screen bg-zinc-950 text-zinc-100 selection:bg-amber-500/30 selection:text-white overflow-x-hidden">
        
        {/* Minimal Fixed Header */}
        <motion.nav 
          className="fixed top-0 left-0 right-0 z-50 px-5 md:px-8"
          style={{ backgroundColor: `rgba(9, 9, 11, ${headerBg})` }}
        >
          <div className="max-w-6xl mx-auto h-16 md:h-20 flex items-center justify-between border-b border-zinc-800/50">
            <div className="flex items-center gap-2.5">
              <img src={auraLogo} alt="AURA" className="w-7 h-7" />
              <span className="text-sm tracking-[0.4em] font-light">AURA</span>
            </div>
            <button 
              onClick={handleGetStarted}
              className="text-xs tracking-[0.2em] uppercase text-amber-400 hover:text-amber-300 transition-colors"
            >
              Start Free →
            </button>
          </div>
        </motion.nav>

        {/* Main Content Landmark */}
        <main>
        {/* HERO: One Word Per Line */}
        <section className="min-h-[100svh] flex flex-col justify-center px-5 md:px-8 pt-20">
          <div className="max-w-6xl mx-auto w-full">
            <motion.p 
              className="text-xs tracking-[0.3em] uppercase text-amber-400/80 mb-6 md:mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Your AI Stylist
            </motion.p>
            
            <h1 className="font-serif text-[15vw] md:text-[12vw] lg:text-[10vw] leading-[0.9] tracking-tight">
              {["Dress.", "Faster."].map((word, i) => (
                <motion.span 
                  key={word}
                  className="block relative"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + i * 0.15, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  {i === 1 ? (
                    <span className="relative inline-block font-light italic text-amber-100">
                      {word}
                      <GoldUnderline delay={0.8} />
                    </span>
                  ) : word}
                </motion.span>
              ))}
            </h1>
            
            <motion.p 
              className="mt-8 md:mt-12 text-base md:text-lg text-zinc-400 max-w-md leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
            >
              Every morning, you stand before a closet full of clothes with nothing to wear. 
              AURA fixes that. One conversation. Done.
            </motion.p>
            
            <motion.div 
              className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1 }}
            >
              <button 
                onClick={handleGetStarted}
                className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-amber-500 text-zinc-950 text-sm font-semibold tracking-wide rounded-full hover:bg-amber-400 transition-colors"
              >
                Get Styled Free
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="text-xs text-zinc-400 self-center">100 free credits • No card required</p>
            </motion.div>
          </div>
          
          {/* Scroll hint */}
          <motion.div 
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
          >
            <div className="w-[1px] h-12 bg-gradient-to-b from-zinc-600 to-transparent" />
          </motion.div>
        </section>

        {/* APP PREVIEW */}
        <Section className="py-16 md:py-24 px-5 md:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="relative flex justify-center">
              <motion.div 
                className="relative w-[280px] md:w-[320px] rounded-[2.5rem] overflow-hidden shadow-2xl shadow-zinc-950/50 border border-zinc-800"
                initial={{ y: 40, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              >
                <img 
                  src={appScreenshot} 
                  alt="AURA App - AI styling your outfits" 
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/60 via-transparent to-transparent pointer-events-none" />
              </motion.div>
              {/* Ambient glow */}
              <div className="absolute -inset-20 bg-[radial-gradient(ellipse_at_center,rgba(251,191,36,0.08),transparent_60%)] pointer-events-none" />
            </div>
          </div>
        </Section>

        {/* THE PROBLEM: One Sentence */}
        <Section className="py-24 md:py-32 px-5 md:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs tracking-[0.3em] uppercase text-amber-400/60 mb-6">The Problem</p>
            <h2 className="font-serif text-2xl md:text-4xl lg:text-5xl leading-snug text-zinc-200">
              Your wardrobe cost thousands.
              <br />
              <span className="relative inline-block text-amber-100">
                You use 20% of it.
                <GoldUnderline delay={0.3} />
              </span>
            </h2>
          </div>
        </Section>

        {/* HOW IT WORKS: Three Cards, Vertical on Mobile */}
        <Section className="py-24 md:py-32 px-5 md:px-8 bg-zinc-900/50">
          <div className="max-w-6xl mx-auto">
            <p className="text-xs tracking-[0.3em] uppercase text-amber-400/60 mb-4 text-center md:text-left">How It Works</p>
            <h2 className="font-serif text-3xl md:text-5xl mb-16 text-center md:text-left">
              Three steps.
              <span className="relative inline-block ml-2 text-amber-100 italic font-light">
                Done.
                <GoldUnderline delay={0.2} />
              </span>
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              {[
                { 
                  num: "01", 
                  title: "Snap", 
                  desc: "Photograph your clothes. AURA recognizes brand, color, and style instantly." 
                },
                { 
                  num: "02", 
                  title: "Ask", 
                  desc: '"What should I wear today?" Just talk. Voice or text. Natural conversation.' 
                },
                { 
                  num: "03", 
                  title: "Wear", 
                  desc: "Get a complete outfit in seconds. Weather-aware. Occasion-perfect. You." 
                },
              ].map((step, i) => (
                <motion.div 
                  key={step.num}
                  className="group p-6 md:p-8 border border-zinc-800 hover:border-amber-500/30 bg-zinc-950/50 transition-all duration-500"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.6 }}
                >
                  <span className="text-5xl font-serif text-zinc-800 group-hover:text-amber-500/20 transition-colors">
                    {step.num}
                  </span>
                  <h3 className="font-serif text-2xl mt-4 mb-3 text-white">{step.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{step.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </Section>

        {/* FEATURES: Asymmetric Grid */}
        <Section className="py-24 md:py-32 px-5 md:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
              <div>
                <p className="text-xs tracking-[0.3em] uppercase text-amber-400/60 mb-4">Why AURA</p>
                <h2 className="font-serif text-3xl md:text-5xl mb-8">
                  Not another
                  <br />
                  <span className="relative inline-block text-amber-100 italic font-light">
                    outfit app.
                    <GoldUnderline delay={0.2} />
                  </span>
                </h2>
                <div className="space-y-5">
                  {[
                    "AI that understands your style, not just trends",
                    "Voice-first. Just talk like you're asking a friend",
                    "Weather, calendar, mood—all considered",
                    "Your data stays yours. Privacy by design",
                  ].map((item, i) => (
                    <motion.div 
                      key={i}
                      className="flex items-start gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <div className="w-5 h-5 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check size={12} className="text-amber-400" />
                      </div>
                      <p className="text-zinc-300">{item}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
              
              <div className="relative">
                <div className="aspect-[3/4] rounded-2xl bg-zinc-900 border border-zinc-800 overflow-hidden">
                  <img 
                    src={appScreenshot} 
                    alt="AURA App Interface" 
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/40 via-transparent to-transparent pointer-events-none" />
                </div>
                <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
              </div>
            </div>
          </div>
        </Section>

        {/* SOCIAL PROOF: Single Quote */}
        <Section className="py-24 md:py-32 px-5 md:px-8 bg-zinc-900/30">
          <div className="max-w-4xl mx-auto text-center">
            <span className="font-serif text-6xl md:text-8xl text-amber-500/10">"</span>
            <blockquote className="font-serif text-xl md:text-3xl text-zinc-200 leading-relaxed -mt-8 mb-8">
              Finally, AI that respects the craft. Every recommendation feels intentional—like it truly understands personal style.
            </blockquote>
            <div>
              <p className="text-sm text-zinc-300">Priya Sharma</p>
              <p className="text-xs text-zinc-500 mt-1">Editorial Director, Vogue India</p>
            </div>
          </div>
        </Section>

        {/* AURA POINTS: Gamification Teaser */}
        <Section className="py-24 md:py-32 px-5 md:px-8">
          <div className="max-w-6xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-500/30 bg-amber-500/5 mb-8">
              <Sparkles size={14} className="text-amber-400" />
              <span className="text-xs tracking-[0.2em] uppercase text-amber-400">Aura Points</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl mb-6">
              Style is a
              <span className="relative inline-block ml-2 text-amber-100 italic font-light">
                journey.
                <GoldUnderline delay={0.2} />
              </span>
            </h2>
            <p className="text-zinc-400 max-w-lg mx-auto mb-12">
              Earn points for every outfit you create and share. Rise from Initiate to Icon. 
              Unlock achievements. Join a community that cares about craft.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {["Initiate", "Curator", "Tastemaker", "Icon"].map((level, i) => (
                <span 
                  key={level}
                  className={`px-4 py-2 text-xs tracking-wider border transition-all ${
                    i === 3 
                      ? "border-amber-500/50 text-amber-400 bg-amber-500/10" 
                      : "border-zinc-800 text-zinc-500"
                  }`}
                >
                  {level}
                </span>
              ))}
            </div>
          </div>
        </Section>

        {/* PRICING: Simple Credits */}
        <Section className="py-24 md:py-32 px-5 md:px-8 bg-zinc-900/50">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xs tracking-[0.3em] uppercase text-amber-400/60 mb-4">Pricing</p>
            <h2 className="font-serif text-3xl md:text-5xl mb-6">
              Pay for what you
              <span className="relative inline-block ml-2 text-amber-100 italic font-light">
                use.
                <GoldUnderline delay={0.2} />
              </span>
            </h2>
            <p className="text-zinc-400 mb-12 max-w-lg mx-auto">
              No subscriptions. No commitments. Buy Style Credits when you need them. 
              Start with 100 free credits—enough to get 6+ complete outfits.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4 max-w-xl mx-auto">
              <div className="p-6 border border-zinc-800 rounded-xl text-left">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Starter</p>
                <p className="text-2xl font-light text-white mb-1">₹99</p>
                <p className="text-sm text-zinc-400">1,000 credits</p>
              </div>
              <div className="p-6 border border-amber-500/30 bg-amber-500/5 rounded-xl text-left relative">
                <span className="absolute -top-2 right-4 text-[10px] px-2 py-0.5 bg-amber-500 text-zinc-950 font-semibold rounded-full">POPULAR</span>
                <p className="text-xs text-amber-400 uppercase tracking-wider mb-2">Pro</p>
                <p className="text-2xl font-light text-white mb-1">₹249</p>
                <p className="text-sm text-zinc-400">3,000 credits</p>
              </div>
            </div>
          </div>
        </Section>

        {/* FINAL CTA */}
        <Section className="py-32 md:py-40 px-5 md:px-8 relative">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(251,191,36,0.05),transparent_70%)]" />
          <div className="max-w-3xl mx-auto text-center relative">
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl mb-6">
              Stop staring.
              <br />
              <span className="relative inline-block text-amber-100 italic font-light">
                Start styling.
                <GoldUnderline delay={0.3} />
              </span>
            </h2>
            <p className="text-zinc-400 mb-10 text-lg">
              Your wardrobe deserves better. Try it free today.
            </p>
            <button 
              onClick={handleGetStarted}
              className="group inline-flex items-center justify-center gap-3 px-10 py-5 bg-amber-500 text-zinc-950 text-sm font-semibold tracking-wide rounded-full hover:bg-amber-400 transition-colors shadow-xl shadow-amber-500/20"
            >
              Get Started Free
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </Section>

        </main>

        {/* FOOTER: Minimal */}
        <footer className="py-12 border-t border-zinc-800/50 px-5 md:px-8">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img src={auraLogo} alt="AURA" className="w-6 h-6" />
              <span className="text-sm tracking-[0.3em] font-light text-zinc-400">AURA</span>
            </div>
            <div className="flex items-center gap-8 text-xs text-zinc-400">
              <a href="/privacy" className="hover:text-zinc-300 transition-colors">Privacy</a>
              <a href="/terms" className="hover:text-zinc-300 transition-colors">Terms</a>
              <a href="/cookies" className="hover:text-zinc-300 transition-colors">Cookies</a>
            </div>
            <div className="flex flex-col items-center md:items-end gap-1">
              <p className="text-xs text-zinc-500">© 2025 Aura Labs</p>
              <a 
                href="https://bwestudios.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[10px] tracking-[0.2em] uppercase text-zinc-600 hover:text-amber-400 transition-colors"
              >
                Built by <span className="font-medium text-zinc-500 hover:text-amber-400">BWE Studio</span>
              </a>
            </div>
          </div>
        </footer>
      </div>

      <OnboardingModal 
        isOpen={showOnboarding} 
        onComplete={handleOnboardingComplete}
        onClose={() => setShowOnboarding(false)}
      />
    </>
  );
};
